import { enqueueSnackbar } from 'notistack';
import { includes, prop } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { getMessage } from '@gef-ui/localization/messages';

import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import dashboardStructure from 'features/views/campaignDashboards/structure';
import contacts from 'features/views/contacts';
import user from 'features/views/user';
// eslint-disable-next-line import/no-cycle
import { history } from 'utils';

import { getDetail } from '../contactDetail/selectors';
import * as types from './actionTypes';
import { CONTACT_NOT_FOUND_ERR_MSG, dialogs, PARTNER_LOCKED } from './constants';

const setCampaignStates = createAction(types.SET_CAMPAIGN_STATES, (states, campaignId) => ({ states, campaignId }));
export const selectedFeedbackState = createAction(types.SELECT_FEEDBACK_STATE, (id) => ({ id }));
export const removeFeedbackState = createAction(types.REMOVE_FEEDBACK_STATE);

const dashboardType = (view) => {
	switch (view) {
		case 'MANAGER':
			return 'dashboard-manager';
		case 'SELLER':
			return 'dashboard-seller';
		case 'PPZ':
			return 'dashboard-ppz';
		default:
			return 'list';
	}
};

const handleReloadsAfterFeedbackUpdate = (view) => (dispatch, getState) => {
	const fetchStorePathUniqueId = `${fetch.constants.urls.contactList}.${dashboardType(view)}`;

	// redirecting to ContactDetail through CampaignManagement detail
	// in CampaignManagement detail - we are fetching contacts directly without any uniqueID
	const wasFetchedDirectlyWithoutUniqueId = fetch.selectors.getLastRequest(fetch.constants.urls.contactList)(
		getState(),
	);
	const wasFetchedWithUniqueId = fetch.selectors.getWasFetched(fetchStorePathUniqueId)(getState());

	if (!isNilOrEmpty(wasFetchedDirectlyWithoutUniqueId)) {
		dispatch(contacts.actions.reload());
	}

	if (wasFetchedWithUniqueId) {
		dispatch(contacts.actions.reload(dashboardType(view)));
	}
};

export const updateFeedbackSuccess = (contact, feedbackStateId) => (dispatch, getState) => {
	const view = dashboardStructure.selectors.getView(getState());
	const { id, campaign } = contact;
	const cameToDetailFrom = prop('cameToDetailFrom', getDetail(getState()));

	const updateFeedback = fetch.actions.put(fetch.constants.urls.contactDetail, {
		uniqueReqID: 'feedback',
		replaceParams: { id },
		body: {
			campaignWaveId: campaign.id,
			feedbackStateId,
			userRole: user.selectors.getCurrentRole(getState()),
		},
		onSuccess: [
			() => () => {
				dispatch(removeFeedbackState());
				dispatch(handleReloadsAfterFeedbackUpdate(view));
				dispatch(contacts.actions.replaceContact(id));
				enqueueSnackbar(getMessage('toastr:approveNonSuccessfulTypeFeedbackUpdate'), { variant: 'success' });
				dispatch(dialog.actions.close(dialogs.PARTNER_LOCKED_DIALOG));
				dispatch(dialog.actions.close(dialogs.PARTNER_NOT_FOUND_IN_CCD));
			},
		],
		onError: [
			({ failure }) =>
				() => {
					if (failure?.parsedContent?.type === PARTNER_LOCKED) {
						dispatch(dialog.actions.open(dialogs.PARTNER_LOCKED_DIALOG));
						dispatch(removeFeedbackState());
					}

					if (includes(CONTACT_NOT_FOUND_ERR_MSG, failure?.parsedContent?.message)) {
						enqueueSnackbar(getMessage('toastr:cannotUpdateContactFeedbackUserIsNotResolver'), {
							variant: 'error',
						});
						dispatch(removeFeedbackState());

						const isOnContactDetail = new RegExp(/^\/contact-detail\/\d+$/);
						if (isOnContactDetail.test(window.location.pathname)) {
							// if user got to detail from contacts
							// force contact refetch on bacground while going back
							if (cameToDetailFrom === '/contacts') {
								dispatch(contacts.actions.reload('list'));
							}

							history.go(-1);
						} else {
							// updating feedback on contactsPage, dashboards, campaignManagement
							dispatch(handleReloadsAfterFeedbackUpdate(view));
						}
					}
				},
		],
		shouldReportError: ({ failure }) => {
			return !(
				failure?.parsedContent?.type === PARTNER_LOCKED ||
				includes(CONTACT_NOT_FOUND_ERR_MSG, failure?.parsedContent?.message)
			);
		},
	});

	dispatch(updateFeedback);
	dispatch(user.actions.fetchAdditionalUserInfo());
};

export const updateFeedback = (contact, feedbackStateId, onSuccess) => (dispatch, getState) => {
	const view = dashboardStructure.selectors.getView(getState());
	const cameToDetailFrom = prop('cameToDetailFrom', getDetail(getState()));

	const { campaign, id } = contact;
	dispatch(
		fetch.actions.put(fetch.constants.urls.contactDetail, {
			uniqueReqID: 'feedback',
			replaceParams: { id: contact.id },
			body: {
				campaignWaveId: campaign.id,
				feedbackStateId,
				userRole: user.selectors.getCurrentRole(getState()),
			},
			onSuccess: [
				() => () => {
					onSuccess(id);
					dispatch(handleReloadsAfterFeedbackUpdate(view));
					enqueueSnackbar(getMessage('toastr:approveNonSuccessfulTypeFeedbackUpdate'), {
						variant: 'success',
					});
					dispatch(dialog.actions.close(dialogs.PARTNER_LOCKED_DIALOG));
					dispatch(dialog.actions.close(dialogs.PARTNER_NOT_FOUND_IN_CCD));
				},
			],
			onError: [
				({ failure }) =>
					() => {
						if (failure?.parsedContent?.type === PARTNER_LOCKED) {
							dispatch(dialog.actions.open(dialogs.PARTNER_LOCKED_DIALOG));
							dispatch(removeFeedbackState());
						}

						if (includes(CONTACT_NOT_FOUND_ERR_MSG, failure?.parsedContent?.message)) {
							enqueueSnackbar(getMessage('toastr:cannotUpdateContactFeedbackUserIsNotResolver'), {
								variant: 'error',
							});
							dispatch(removeFeedbackState());

							const isOnContactDetail = new RegExp(/^\/contact-detail\/\d+$/);
							if (isOnContactDetail.test(window.location.pathname)) {
								// if user got to detail from contacts
								// force contact refetch on bacground while going back
								if (cameToDetailFrom === '/contacts') {
									dispatch(contacts.actions.reload('list'));
								}
								history.go(-1);
							} else {
								// updating feedback on contactsPage, dashboards, campaignManagement
								dispatch(handleReloadsAfterFeedbackUpdate(view));
							}
						}
					},
			],
			shouldReportError: ({ failure }) => {
				return !(
					failure?.parsedContent?.type === PARTNER_LOCKED ||
					includes(CONTACT_NOT_FOUND_ERR_MSG, failure?.parsedContent?.message)
				);
			},
		}),
	);
};

export const getCampaignStates = (campaignId) => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.campaignStatus, {
			replaceParams: { id: campaignId },
			onSuccess: [(response) => setCampaignStates(response.body, campaignId)],
		}),
	);
};

export const showPartnerNotFoundInCCDDialog = () => (dispatch) => {
	dispatch(dialog.actions.open(dialogs.PARTNER_NOT_FOUND_IN_CCD));
};
